import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ArrowUpDown } from "lucide-react";

export default function MyBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortDir, setSortDir] = useState("desc"); // "desc" newest first, "asc" oldest first

    const load = async () => {
        const r = await api.get("/bookings/me");
        setBookings(r.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const sorted = useMemo(() => {
        return [...bookings].sort((a, b) => {
            const cmp = a.date.localeCompare(b.date);
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [bookings, sortDir]);

    const cancel = async (id) => {
        try {
            const r = await api.post(`/bookings/${id}/cancel`);
            toast.success(r.data.refunded ? "Cancelled — credits refunded" : "Cancelled (no refund)");
            await load();
        } catch (e) {
            toast.error("Cancel failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    return (
        <div className="px-6 md:px-12 lg:px-16 py-10 max-w-6xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-2">My bookings</p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">All sessions</h1>
                    <p className="text-base text-slate-600 mt-2">Cancel up to 24 hours before start to recover credits.</p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    data-testid="bookings-sort-toggle"
                    className="rounded-md h-10 self-start md:self-auto"
                >
                    <ArrowUpDown className="h-4 w-4 mr-2" strokeWidth={1.75} />
                    Sort by date · {sortDir === "desc" ? "newest first" : "oldest first"}
                </Button>
            </div>

            <div className="mt-8 bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Date</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Slot</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Credits</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Status</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>}
                        {!loading && sorted.length === 0 && (
                            <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No bookings yet.</td></tr>
                        )}
                        {sorted.map((b) => {
                            const canCancel = b.status === "confirmed" && parseISO(b.date) >= new Date(new Date().toDateString());
                            return (
                                <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                                    <td className="px-5 py-4 font-medium text-slate-900">{format(parseISO(b.date), "EEE, MMM d, yyyy")}</td>
                                    <td className="px-5 py-4 text-slate-600 font-mono text-xs uppercase tracking-[0.14em]">{b.slot.replace("_", " ")}</td>
                                    <td className="px-5 py-4 text-slate-600">{b.credits_used}</td>
                                    <td className="px-5 py-4">
                                        <StatusBadge status={b.status} />
                                    </td>
                                    <td className="px-5 py-4 text-right">
                                        {canCancel && (
                                            <Button variant="outline" size="sm" onClick={() => cancel(b.id)} data-testid={`cancel-booking-${b.id}`} className="rounded-md">
                                                Cancel
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        cancelled: "bg-slate-50 text-slate-500 border-slate-200",
        completed: "bg-slate-100 text-slate-700 border-slate-200",
        no_show: "bg-red-50 text-red-700 border-red-200",
        overrun_charged: "bg-amber-50 text-amber-700 border-amber-200",
        pending_admin_approval: "bg-amber-50 text-amber-700 border-amber-200",
        rejected_admin: "bg-red-50 text-red-700 border-red-200",
    };
    const labels = {
        pending_admin_approval: "Pending Approval",
        rejected_admin: "Not Approved",
    };
    return (
        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-mono border rounded-sm ${map[status] || "bg-slate-50 text-slate-700"}`}>
            {labels[status] || status.replace(/_/g, " ")}
        </span>
    );
}
